/**
 * Firestore data access for the customer app. All reads are gated by security
 * rules (approved dealers only for products). We minimize reads (feature #5/#6)
 * by listening ONLY to the category in view, never the whole catalog.
 */
import {
  deleteField,
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  type Unsubscribe,
} from 'firebase/firestore';
import type {
  AppConfig,
  DisplayFormatConfig,
  Product,
  ScratchCard,
  ScratchCardConfig,
  User,
} from '@mitang/shared';
import { COLLECTIONS, DISPLAY_FORMAT_DOC, FCM_TOKEN_LIMIT, paths, WISHLIST_MAX } from '@mitang/shared';
import { catalogEndpoint } from './env';
import type { InstalledApp } from './platform';
import { auth, db } from './firebase';

/* ------------------------------ registration ------------------------------ */

/**
 * Profile fields collected by the post-OTP registration form. Identity fields
 * (`id`, `status`, `fcmTokens`, `createdAt`) are set by
 * `createOwnUserProfile`; `email` comes from the verified sign-in token.
 */
export type NewUserProfile = Pick<
  User,
  'name' | 'mobile' | 'email' | 'companyName' | 'city'
> & { gstNumber?: string };

/**
 * FREE-TIER MODEL: there are no Cloud Functions, so registration is now a
 * DIRECT client write of the caller's own `users/{uid}` doc. Security rules
 * allow a signed-in user to CREATE their own doc only with `status: 'pending'`,
 * `id === uid`, and `email` equal to the verified token email. This runs AFTER
 * email-OTP sign-in, so `uid` is `auth.currentUser.uid`.
 */
export async function createOwnUserProfile(
  uid: string,
  input: NewUserProfile,
  // 'approved' is allowed by the rules ONLY while the admin approval toggle
  // (app_config.approvalRequired) is off.
  status: 'pending' | 'approved' = 'pending',
): Promise<void> {
  const payload: User = {
    id: uid,
    status,
    name: input.name,
    mobile: input.mobile,
    email: input.email,
    companyName: input.companyName,
    city: input.city,
    ...(input.gstNumber ? { gstNumber: input.gstNumber } : {}),
    fcmTokens: [],
    createdAt: Date.now(),
  };
  await setDoc(doc(db, paths.user(uid)), payload);
}

/* ------------------------------ profile edit ------------------------------ */

/**
 * Fields a dealer may self-edit on their own users/{uid} doc — never `status`,
 * and never `email` (it is the verified login identity; rules reject changes).
 */
export type ProfileEdit = Partial<
  Pick<User, 'name' | 'mobile' | 'companyName' | 'city' | 'gstNumber'>
>;

/**
 * Self-edit of the caller's own `users/{uid}` doc. Rules allow a user to UPDATE
 * their own doc as long as `status`, `email` (and `id`) are unchanged, so the
 * remaining profile fields and `fcmTokens` are self-writable.
 */
export async function updateOwnUserProfile(
  uid: string,
  edit: ProfileEdit,
): Promise<void> {
  await setDoc(doc(db, paths.user(uid)), edit, { merge: true });
}

/**
 * Stamp "I'm active now" on the caller's own doc (admin Users page's
 * "Last active", Insights churn math, and the active7d push audience all read
 * this). Self-update is allowed by the rules (status/email untouched).
 *
 * When `installed` is given, the same write records this device in ITS OWN
 * slot of `installs` (admin "App versions" page) using a dotted field path, so
 * a Play shell writes `installs.play`, a sideloaded shell `installs.sideload`
 * and a browser `installs.web` — none of them can touch the others. A dealer
 * who has the Play app and also opens the website therefore keeps their Play
 * record intact.
 */
export async function touchLastActive(uid: string, installed?: InstalledApp): Promise<void> {
  const now = Date.now();
  const app: Record<string, unknown> = {};
  if (installed?.channel === 'web') {
    app['installs.web'] = { seenAt: now };
  } else if (installed) {
    app[`installs.${installed.channel}`] = {
      seenAt: now,
      ...(installed.build !== undefined ? { build: installed.build } : {}),
      ...(installed.version ? { version: installed.version } : {}),
    };
  }
  if (installed) {
    // One-off cleanup of the short-lived flat fields (Sep 2026) that a browser
    // visit could overwrite. Harmless no-ops once gone; drop after Oct 2026.
    for (const legacy of ['appChannel', 'appBuild', 'appVersion', 'appSeenAt', 'webSeenAt']) {
      app[legacy] = deleteField();
    }
  }
  await updateDoc(doc(db, paths.user(uid)), { lastActiveAt: now, ...app });
}

/* ------------------------------- fcm tokens ------------------------------- */

/**
 * FREE-TIER MODEL: no `registerFcmToken` callable. Persist this device's FCM
 * token directly on the caller's own `users/{uid}` doc via `arrayUnion` (rules
 * allow the self-update since `status`/`id` are untouched).
 *
 * FCM_TOKEN_LIMIT is enforced client-side: if the array would exceed the limit
 * we read-modify-write, keeping the newest tokens (drop the oldest).
 */
export async function addOwnFcmToken(uid: string, token: string): Promise<void> {
  const ref = doc(db, paths.user(uid));
  const snap = await getDoc(ref);
  const existing = snap.exists() ? ((snap.data() as User).fcmTokens ?? []) : [];
  if (existing.includes(token)) return;

  if (existing.length + 1 > FCM_TOKEN_LIMIT) {
    // Read-modify-write to prune oldest, appending the new token at the end.
    const pruned = [...existing, token].slice(-FCM_TOKEN_LIMIT);
    await updateDoc(ref, { fcmTokens: pruned });
    return;
  }
  await updateDoc(ref, { fcmTokens: arrayUnion(token) });
}

/** Remove this device's FCM token (e.g. on logout) from the caller's own doc. */
export async function removeOwnFcmToken(uid: string, token: string): Promise<void> {
  await updateDoc(doc(db, paths.user(uid)), { fcmTokens: arrayRemove(token) });
}

/* -------------------------------- products -------------------------------- */

/**
 * Ask the sync worker to read the spreadsheet RIGHT NOW (pull-to-refresh),
 * instead of waiting for its once-a-minute pass. Rate-limited server-side, and
 * best-effort: if it fails we still show whatever the last sync produced.
 */
export async function requestSyncNow(): Promise<void> {
  if (!catalogEndpoint) return;
  const token = await auth.currentUser?.getIdToken().catch(() => null);
  if (!token) return;
  try {
    await fetch(catalogEndpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ action: 'refresh', token }),
    });
  } catch {
    // Offline or worker unreachable — the reload still refreshes from cache.
  }
}

/**
 * Fetch specific products by id — the LAST-RESORT path for the Saved screen.
 *
 * Saved products are normally resolved for free from the category catalogs
 * already cached on the device. This covers the gap: a fresh install, or a
 * category the dealer has never opened. Chunked because Firestore allows at most
 * 30 values in an `in` filter, and bounded by WISHLIST_MAX either way, so the
 * cost can never scale with the size of the catalogue.
 */
export async function fetchProductsByIds(ids: string[]): Promise<Product[]> {
  const unique = [...new Set(ids)].slice(0, WISHLIST_MAX);
  if (unique.length === 0) return [];
  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += 30) chunks.push(unique.slice(i, i + 30));

  const results = await Promise.all(
    chunks.map((chunk) =>
      getDocs(query(collection(db, COLLECTIONS.products), where(documentId(), 'in', chunk)))
        .then((snap) => snap.docs.map((d) => d.data() as Product))
        .catch(() => [] as Product[]),
    ),
  );
  return results.flat();
}

/* -------------------------------- wishlist -------------------------------- */

/**
 * Replace the signed-in dealer's saved-product list.
 *
 * Written to the dealer's OWN user document, which is deliberate:
 *  - their saved screen then costs zero extra reads, because AuthContext already
 *    streams this document (a `wishlists` collection would cost one read per
 *    saved item every time the screen opened);
 *  - nothing writes to a document shared between dealers, so there is no
 *    ~1-write-per-second hotspot to contend on — the admin derives counts from
 *    the user list it already holds instead of a per-product counter.
 *
 * Two fields move together: `wishlist` (an array, so the admin can run
 * array-contains to find everyone who saved a product) and `wishlistAt` (a map
 * of productId → timestamp, because an array cannot carry queryable per-item
 * dates). Newest first, capped at WISHLIST_MAX.
 *
 * Takes the FINAL list rather than one product so a burst of taps collapses into
 * a single write (see hooks/useWishlist.ts).
 */
export async function writeWishlist(user: User, nextIds: string[]): Promise<void> {
  // Deduplicate defensively and enforce the cap here rather than trusting the
  // caller, since this is the only door into the field.
  const next = [...new Set(nextIds)].slice(0, WISHLIST_MAX);

  // Rebuild the timestamp map from `next` so it can never accumulate entries for
  // products that have since been removed.
  const previousAt = user.wishlistAt ?? {};
  const now = Date.now();
  const nextAt: Record<string, number> = {};
  for (const id of next) nextAt[id] = previousAt[id] ?? now;

  await setDoc(
    doc(db, paths.user(user.id)),
    { wishlist: next, wishlistAt: nextAt },
    { merge: true },
  );
}

/**
 * SELF-SERVICE ACCOUNT DELETION (Google Play requirement).
 *
 * Deletes the dealer's profile, presence, price access, activity history, Clerk
 * identity and Firebase sign-in — permanently, not a deactivation. The dealer's
 * own Firestore credentials deliberately cannot reach most of that, so the sync
 * worker does the work under its service account after verifying the ID token.
 *
 * Throws with a human-readable message on failure so the caller can show it.
 */
export async function deleteOwnAccount(): Promise<void> {
  if (!catalogEndpoint) throw new Error('Account deletion is unavailable right now.');
  const token = await auth.currentUser?.getIdToken().catch(() => null);
  if (!token) throw new Error('Please sign in again and retry.');

  let res: Response;
  try {
    res = await fetch(catalogEndpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ action: 'delete_own_account', token }),
    });
  } catch {
    throw new Error('No connection. Please try again when you are online.');
  }
  const payload = (await res.json().catch(() => null)) as { ok?: boolean; message?: string } | null;
  if (!res.ok || !payload?.ok) {
    throw new Error(payload?.message ?? 'Could not delete your account. Please try again.');
  }
}

/**
 * EDGE CATALOG (primary read path). One cached request per category from the
 * sync worker, instead of a Firestore document read per product — this is what
 * keeps the app inside the free tier at thousands of dealers. Realtime is
 * preserved by `app_config.catalogVersion`: the app watches that single doc and
 * re-fetches with a new `v` whenever it moves.
 *
 * Returns null when the catalog isn't reachable (not synced yet, offline, no
 * token) so callers can fall back to the Firestore listener.
 */
export async function fetchCategoryCatalog(
  category: string,
  version: number | undefined,
): Promise<Product[] | null> {
  if (!catalogEndpoint) return null;
  const token = await auth.currentUser?.getIdToken().catch(() => null);
  if (!token) return null;
  const url =
    `${catalogEndpoint}/catalog?category=${encodeURIComponent(category)}` +
    `&v=${version ?? 0}&token=${encodeURIComponent(token)}`;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = (await res.json()) as {
      products?: Array<Omit<Product, 'category' | 'active' | 'newPrice'>>;
    };
    if (!data.products) return null;
    return data.products.map((p) => ({
      ...p,
      category,
      active: true,
      newPrice: null,
    })) as Product[];
  } catch {
    return null;
  }
}

/**
 * Listen to a single category's active products, ordered to match the composite
 * index (category, active, updatedAt DESC). Returns an unsubscribe fn.
 */
export function listenCategoryProducts(
  category: string,
  onChange: (products: Product[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, COLLECTIONS.products),
    where('category', '==', category),
    where('active', '==', true),
    orderBy('updatedAt', 'desc'),
  );
  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Product, 'id'>) }));
      onChange(items);
    },
    (err) => onError(err as Error),
  );
}

/**
 * Listen to a single product doc (deep-link target `product/{id}`). Realtime so
 * the detail screen reflects live price changes too.
 */
export function listenProduct(
  id: string,
  onChange: (product: Product | null) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const ref = doc(db, paths.product(id));
  return onSnapshot(
    ref,
    (snap) =>
      onChange(snap.exists() ? { id: snap.id, ...(snap.data() as Omit<Product, 'id'>) } : null),
    (err) => onError(err as Error),
  );
}

/** One-shot product read (used when a listener isn't warranted). */
export async function getProduct(id: string): Promise<Product | null> {
  const snap = await getDoc(doc(db, paths.product(id)));
  return snap.exists() ? { id: snap.id, ...(snap.data() as Omit<Product, 'id'>) } : null;
}

/**
 * Listen to the set of distinct categories among active products, so the Home
 * tabs are dynamic (feature #4). We derive categories from a lightweight
 * listener over active products (id + category only is not possible with the
 * Web SDK, so we read the docs but only use their category field). To keep this
 * cheap for large catalogs, a future optimization is a maintained
 * `app_config.categories` array written by Agent A's ingest.
 *
 * SHARED-CONTRACT WISH (flag for Lead Agent): expose a `categories: string[]`
 * (and optional per-category counts) on `app_config/global` so the app doesn't
 * scan all products just to build tabs.
 */
export function listenCategories(
  onChange: (categories: string[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(collection(db, COLLECTIONS.products), where('active', '==', true));
  return onSnapshot(
    q,
    (snap) => {
      const set = new Set<string>();
      snap.forEach((d) => {
        const c = (d.data() as { category?: string }).category;
        if (c) set.add(c);
      });
      onChange(Array.from(set).sort((a, b) => a.localeCompare(b)));
    },
    (err) => onError(err as Error),
  );
}

/* ------------------------------- app config ------------------------------- */

export function listenAppConfig(
  onChange: (config: AppConfig | null) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const ref = doc(db, paths.appConfig());
  return onSnapshot(
    ref,
    (snap) => onChange(snap.exists() ? (snap.data() as AppConfig) : null),
    (err) => onError(err as Error),
  );
}

/**
 * Listen to app_config/displayFormat (per-tab labels, table gaps, share-message
 * layout). Signed-in read per the rules; changes apply to the UI instantly.
 */
export function listenDisplayFormat(
  onChange: (config: DisplayFormatConfig | null) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const ref = doc(db, COLLECTIONS.appConfig, DISPLAY_FORMAT_DOC);
  return onSnapshot(
    ref,
    (snap) => onChange(snap.exists() ? (snap.data() as DisplayFormatConfig) : null),
    (err) => onError(err as Error),
  );
}

/* ------------------------------ scratch & win ----------------------------- */

/**
 * The admin's scratch-card switch + prize table (`app_config/scratchCard`).
 * Signed-in read; the app reacts live when the admin turns it on or off.
 */
export function listenScratchConfig(
  onChange: (config: ScratchCardConfig | null) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const ref = doc(db, paths.scratchCardConfig());
  return onSnapshot(
    ref,
    (snap) => onChange(snap.exists() ? (snap.data() as ScratchCardConfig) : null),
    (err) => onError(err as Error),
  );
}

/**
 * This dealer's card for the given round. The id is deterministic
 * (`{round}_{uid}`), so no query is needed — one cheap doc listener that also
 * reflects the dealer's own `revealedAt` write instantly.
 */
export function listenMyScratchCard(
  round: string,
  uid: string,
  onChange: (card: ScratchCard | null) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const ref = doc(db, paths.scratchCard(round, uid));
  return onSnapshot(
    ref,
    (snap) =>
      onChange(snap.exists() ? { ...(snap.data() as Omit<ScratchCard, 'id'>), id: snap.id } : null),
    (err) => onError(err as Error),
  );
}

/**
 * Every card this dealer has ever drawn (all rounds), newest first — the
 * "your rewards" history. Rules allow the list only when filtered to the
 * caller's own uid, which is exactly this query.
 */
export function listenMyScratchCards(
  uid: string,
  onChange: (cards: ScratchCard[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(collection(db, COLLECTIONS.scratchCards), where('uid', '==', uid));
  return onSnapshot(
    q,
    (snap) =>
      onChange(
        snap.docs
          .map((d) => ({ ...(d.data() as Omit<ScratchCard, 'id'>), id: d.id }))
          .sort((a, b) => b.drawnAt - a.drawnAt),
      ),
    (err) => onError(err as Error),
  );
}

/**
 * Ask the sync worker to draw this dealer's card for the current round.
 *
 * The prize is chosen SERVER-SIDE with the admin's weights and written once per
 * dealer per round (the worker refuses a second card, and simply returns the
 * first one if the dealer taps twice or opens two devices). The app never
 * decides a prize — it only reveals it. Throws with a human-readable message.
 */
export async function drawScratchCard(): Promise<ScratchCard> {
  if (!catalogEndpoint) throw new Error('Scratch & win is unavailable right now.');
  const token = await auth.currentUser?.getIdToken().catch(() => null);
  if (!token) throw new Error('Please sign in again and retry.');

  let res: Response;
  try {
    res = await fetch(catalogEndpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ action: 'scratch_draw', token }),
    });
  } catch {
    throw new Error('No connection. Connect to the internet to get your card.');
  }
  const payload = (await res.json().catch(() => null)) as
    | { ok?: boolean; card?: ScratchCard; message?: string }
    | null;
  if (!res.ok || !payload?.ok || !payload.card) {
    throw new Error(payload?.message ?? 'Could not get your card. Please try again.');
  }
  return payload.card;
}

/**
 * Stamp "scratched" on the dealer's own card. `revealedAt` is the ONE field the
 * security rules let a dealer write on a card — the prize itself is immutable.
 * Offline-safe: Firestore queues it and the local listener reflects it at once.
 */
export async function revealScratchCard(card: ScratchCard): Promise<void> {
  await updateDoc(doc(db, paths.scratchCard(card.round, card.uid)), { revealedAt: Date.now() });
}
